import api from './api';
import type { AcctDrillRow, AcctMemberType } from './accountingDashboards.service';

/**
 * Per-invoice checkbox creation exists on ACCESSORIAL buckets only.
 *
 * V1 draws this column one of two ways (formatview.pl::fv_listinvoice): on the accessorial
 * bucket screen (`$action eq 'showbucketacctaccessorial'`) it is a checkbox per invoice, and
 * on every other bucket it is a C/V link to the List Invoice page. Order-backed buckets
 * therefore create from that page, never from the drill — which also keeps creation honest
 * about grouping, since a `pro` bucket lists ONE representative pro per masterpro and
 * billing it alone would contradict `billtos.invoiceseparately='N'` (bill the masterpro as one
 * invoice). Storage and EDI have separate workflows; their drills are placeholders.
 */
export function canCreateBucketInvoices(key: string, memberType: AcctMemberType | null): boolean {
  if (memberType !== 'invoice') return false;
  return /^acct-pinv-(accessorials|per-diem|chassis)$/.test(key)
    || /^acct-acc-(not-invoiced(?:-per-diem)?|(?:not-)?delivered(?:-inv-(?:gt0|eq0))?)$/.test(key);
}

function positiveId(value: unknown): boolean {
  return /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0;
}

/** Display-time eligibility only. The server reviews current holds and rechecks before creation. */
export function canSelectInvoice(row: AcctDrillRow): boolean {
  return positiveId(row.invoice_recno) && positiveId(row.invoice_billto)
    && row.invoice_deleted === 'N' && Number(row.invhistoryrecno) === 0
    && !row.invoice_date && row.billfromsystem === 'CMS';
}

export function invoiceSelectionError(rows: AcctDrillRow[]): string | null {
  if (!rows.length) return 'Select at least one invoice.';
  if (rows.length > 500) return 'Select up to 500 invoice rows per batch.';
  if (rows.some((row) => !canSelectInvoice(row))) return 'Some selected rows are no longer available for invoicing. Refresh and select them again.';
  if (new Set(rows.map((row) => Number(row.invoice_billto))).size > 1) {
    return 'You may only bill one Bill To at a time. Select invoices from one Bill To.';
  }
  return null;
}

export type InvoiceClass = '' | 'A' | 'S' | 'R';

/** V1's `@invoicetype` (defs.pl:4060), in its order. Shared so the two dropdowns cannot
 *  drift: '' is MASTERPRO, not "Standard", and 'S' is SUPPLEMENTAL, not "Storage" —
 *  storage invoices are a separate screen keyed on an ST masterpro. */
export const INVOICE_CLASS_OPTIONS: ReadonlyArray<{ value: InvoiceClass; label: string }> = [
  { value: '', label: 'Masterpro' },
  { value: 'A', label: 'Accessorial' },
  { value: 'S', label: 'Supplemental' },
  { value: 'R', label: 'Rebill' },
];

/** A planned invoice the holds refuse. Evaluated per charge, so one bad PRO no longer
 *  refuses the rest of the run. */
export interface HeldInvoice {
  invoiceRecnos: number[];
  pronumbers: number[];
  total: string;
  reasons: string[];
}

export interface InvoiceCreationReview {
  snapshot: string;
  invoiceClass: InvoiceClass;
  billto: number;
  billtoName: string;
  canCreate: boolean;
  holds: string[];
  skipped: string[];
  warnings: string[];
  /** How many invoices the run will actually RAISE — held charges are already excluded. */
  invoiceCount: number;
  /** ...and how many it will leave behind for the user to fix and re-run. */
  heldCount: number;
  heldItems: HeldInvoice[];
  /** What the selection planned before the holds were applied. */
  plannedCount: number;
  total: string;
  excludedRecnos: number[];
  rows: { invoiceRecno: number; pronumber: number; accessorialNum: number; container: string; total: string; included: boolean }[];
}

export interface InvoiceCreationRequest {
  invoiceRecnos: number[];
  invoiceClass: InvoiceClass;
  snapshot: string;
  requestId: string;
  /** Send one e-mail per raised invoice. Only honoured for a Bill To configured for it —
   *  the server gate is `billtos.invoiceseparately_email`, this only lets a user decline. */
  emailInvoices?: boolean;
}

export interface InvoiceCreationResult {
  invoices: { recno: number; invoiceNumber: string; total: string }[];
  excludedRecnos: number[];
  /** Charges the authoritative run refused. Reported from the create, not the preview —
   *  a charge can go on hold between the two. */
  heldItems?: HeldInvoice[];
  /** The queued e-mail batch, or null when nothing will be sent. Sending outlives the
   *  request, so this is a handle to poll rather than a result. */
  emailBatch?: number | null;
}

export interface MassEmailStatus {
  batch: number;
  status: 'pending' | 'sending' | 'done';
  billto: number;
  totalItems: number;
  recipients: string[];
  sent: number;
  failed: { invoiceNumber: string; error: string }[];
  pending: number;
}

export async function loadMassEmailStatus(batch: number): Promise<MassEmailStatus> {
  return (await api.post<MassEmailStatus>('/invoice-list/mass-email/status', { batch })).data;
}

export async function previewInvoiceCreation(invoiceRecnos: number[], invoiceClass?: InvoiceClass): Promise<InvoiceCreationReview> {
  return (await api.post<InvoiceCreationReview>('/invoice-list/creation-preview', {
    invoiceRecnos: [...new Set(invoiceRecnos)], invoiceClass,
  })).data;
}

export async function createInvoices(request: InvoiceCreationRequest): Promise<InvoiceCreationResult> {
  return (await api.post<InvoiceCreationResult>('/invoice-list/create', request)).data;
}

/**
 * POST expecting a PDF back, with the error case made readable.
 *
 * `responseType: 'blob'` applies to the ERROR response too, so a 409 arrives with its
 * JSON body wrapped in a Blob — `response.data.detail` is undefined and every failure
 * renders as "Request failed with status code 409", whatever the server actually said.
 * Unwrap it in place so callers read the detail the same way they do on every other call.
 */
async function postForPdf(path: string, body: unknown): Promise<Blob> {
  try {
    return (await api.post<Blob>(path, body, { responseType: 'blob' })).data;
  } catch (error) {
    const response = (error as { response?: { data?: unknown } })?.response;
    if (response && response.data instanceof Blob) {
      try {
        response.data = JSON.parse(await response.data.text());
      } catch {
        // Not JSON — a gateway error page, say. Leave it; the status still surfaces.
      }
    }
    throw error;
  }
}

/** The invoice PDF, with any ticked backup paperwork merged in behind it. V1 merges the
 *  documents into the invoice itself on the one path that views, prints and e-mails
 *  (invoicepdf.pl:1736), so ticking paperwork changes what View shows, not only what an
 *  e-mail carries. A document that cannot be read fails the whole call by name. */
export async function printInvoices(
  invoiceHistoryRecnos: number[],
  fileRecnos: number[] = [],
): Promise<Blob> {
  return postForPdf('/invoice-list/print', {
    invoiceHistoryRecnos: [...new Set(invoiceHistoryRecnos)],
    fileRecnos: [...new Set(fileRecnos)],
  });
}

/** The invoice PDF plus the filename the SERVER chose for it, read off
 *  Content-Disposition (exposed through CORS in main.py). Used where the name cannot be
 *  known in advance — a create run, where the invoice number does not exist until the
 *  invoices do. One rule for the name, living on the server. */
export async function printInvoicesNamed(
  invoiceHistoryRecnos: number[],
  fileRecnos: number[] = [],
): Promise<{ blob: Blob; filename: string }> {
  const response = await api.post('/invoice-list/print', {
    invoiceHistoryRecnos: [...new Set(invoiceHistoryRecnos)],
    fileRecnos: [...new Set(fileRecnos)],
  }, { responseType: 'blob' });
  const disposition = String(response.headers['content-disposition'] ?? '');
  const match = /filename="?([^";]+)"?/.exec(disposition);
  return { blob: response.data as Blob, filename: match ? match[1] : 'invoice.pdf' };
}

/** A named, time-limited URL for the same PDF `printInvoices` returns as bytes.
 *
 *  Used by View. A tab showing a `URL.createObjectURL` blob has no name — the browser's
 *  PDF viewer offers the blob URL's own UUID as the Save As filename — so the file has to
 *  arrive over something that carries one. The URL is the credential: read-only, one
 *  file, thirty minutes. Do not store it or put it in a link anyone keeps. */
export async function invoiceViewUrl(
  invoiceHistoryRecnos: number[],
  fileRecnos: number[] = [],
): Promise<{ url: string; filename: string }> {
  const response = await api.post('/invoice-list/view-url', {
    invoiceHistoryRecnos: [...new Set(invoiceHistoryRecnos)],
    fileRecnos: [...new Set(fileRecnos)],
  });
  return response.data as { url: string; filename: string };
}

export interface InvoiceCancellation {
  invhistoryrecno: number;
  masterpro: string;
  billto: number;
  /** How many `invoice` rows went back to uninvoiced. */
  releasedCharges: number;
  pronumbers: number[];
}

/**
 * Cancel a raised invoice (lib_invoice.pl::docancelinvoice). The charges are RELEASED, not
 * destroyed, so the same work can be invoiced again; the archived PDF survives. The reason
 * is required — it becomes a note on every pro on the invoice.
 */
export async function cancelInvoice(invoiceHistoryRecno: number, reason: string): Promise<InvoiceCancellation> {
  return (await api.post<InvoiceCancellation>('/invoice-list/cancel', {
    invoiceHistoryRecno, reason,
  })).data;
}

export interface InvoiceBackupDocument {
  pronumber: number;
  /** fileinfo recno — what the merge and the e-mail are keyed by. */
  fileRecno: number;
  /** cms_map recno — what the PREVIEW route is addressed by. The two share an integer
   *  space and name different rows, so previewing by `fileRecno` shows somebody else's
   *  file. Optional: a cached bundle can outlive a deploy and meet an API without it. */
  mapRecno?: number;
  filename: string;
  filetype: string;
  documenttype: string;
  uploaded: string;
  description: string;
}

export interface InvoiceOptionsData {
  billto: number;
  billtoEmails: string[];
  userEmail: string;
  /** The Bill To invoices each charge separately, so there is no single PDF to view. */
  invoicesSeparately: boolean;
  /** ...and every one of those invoices will be e-mailed to the Bill To. */
  emailsEachInvoice: boolean;
  documents: { pronumber: number; documents: InvoiceBackupDocument[] }[];
  documentTypes: string[];
}

export interface InvoiceOptionsQuery {
  billto: number;
  invoiceClass?: InvoiceClass;
  /** An invoice that exists. Omit and pass `invoiceRecnos` for one about to be created. */
  invoiceHistoryRecno?: number;
  invoiceRecnos?: number[];
}

export async function loadInvoiceOptions(query: InvoiceOptionsQuery): Promise<InvoiceOptionsData> {
  return (await api.post<InvoiceOptionsData>('/invoice-list/options', query)).data;
}

export interface InvoiceEmailRequest {
  invoiceHistoryRecno: number;
  toSelf: boolean;
  toBillto: string[];
  toOther: string;
  fileRecnos: number[];
}

export interface InvoiceEmailResult {
  invhistoryrecno: number;
  subject: string;
  /** Who it actually went to — the sender alone outside production. */
  recipients: string[];
  /** Who it would go to in production. Same as `recipients` when not redirected. */
  intendedRecipients: string[];
  /** True outside production: nothing reached the customer. */
  redirected: boolean;
  attachments: string[];
}

export async function emailInvoice(request: InvoiceEmailRequest): Promise<InvoiceEmailResult> {
  return (await api.post<InvoiceEmailResult>('/invoice-list/email', request)).data;
}

/** V1's three EDI controls on the Invoice List (lib_invoice.pl:1226-1250). Nothing is
 *  sent here: each flips one flag on `cms.edi_sentinvoices` that the EDI script reads on
 *  its next run. Only the action the current state offers is accepted — a stale tab gets
 *  a 409 with the state that surprised it rather than silently undoing somebody else. */
export type EdiSendAction = 'resend' | 'cancel_resend' | 'send';

export interface EdiSendResult {
  recno: number;
  sent: string;
  resend: string;
  donotsend: string;
  action: '' | EdiSendAction;
  /** The label of the control that was applied, for the confirmation line. */
  applied: string;
}

export async function requestEdiSend(
  invoiceHistoryRecno: number,
  action: EdiSendAction,
): Promise<EdiSendResult> {
  return (await api.post<EdiSendResult>('/invoice-list/edi-send', { invoiceHistoryRecno, action })).data;
}

/** V1's View Estimate (lib_invoice.pl:726 -> $printtest). The invoice these charges
 *  WOULD produce, rendered and returned, creating nothing. Deliberately available while
 *  the invoice is on hold — quoting a number before the paperwork is settled is the use
 *  case — and deliberately without backup paperwork, which V1 never attaches here. */
export async function renderEstimate(invoiceRecnos: number[]): Promise<Blob> {
  return postForPdf('/invoice-list/estimate', { invoiceRecnos });
}

export interface EstimateEmailRequest {
  invoiceRecnos: number[];
  toSelf: boolean;
  toBillto: string[];
  toOther: string;
}

export async function emailEstimate(request: EstimateEmailRequest): Promise<InvoiceEmailResult> {
  return (await api.post<InvoiceEmailResult>('/invoice-list/estimate-email', request)).data;
}
